/**
 * ConsentService: Centralized JavaScript module for managing user consents for Sofabuddy features
 * This service handles the API calls, UI updates, and state management for chatbot and microphone consent
 */
import makeRequest from '@lib/fetchService';

class SofabuddyConsentService {
  constructor() {
    this.amplitudeEventConsent = null;
    this.eventPropertiesConsent = null;
    this.redirectPathConsentGiven = null;
  }

  // Initialize with optional event tracking data
  init(options = {}) {
    this.amplitudeEventConsent = options.amplitudeEventConsent;
    this.eventPropertiesConsent = options.eventPropertiesConsent;
    this.redirectPathConsentGiven = options.redirectPathConsentGiven;
    this._bindEvents();
  }

  // Set up event listeners for consent-related UI elements
  _bindEvents() {
    // Bind chatbot consent buttons
    const consentButtons = document.querySelectorAll(
      '.js-sofabuddy-consent-button'
    );
    consentButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.setChatbotConsent(button.dataset.chatbotConsent, true);
      });
    });

    // Bind microphone consent buttons
    const microphoneConsentButtons = document.querySelectorAll(
      '.js-microphone-consent-button'
    );
    microphoneConsentButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.setMicrophoneConsent(button.dataset.value);
      });
    });

    // Synchronize microphone consent checkboxes
    const microphoneConsentCheckboxes = document.querySelectorAll(
      '.js-microphone-consent-checkbox'
    );
    microphoneConsentCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', function() {
        microphoneConsentCheckboxes.forEach(cb => {
          cb.checked = this.checked;
        });
      });
    });
  }

  // Wrapper for tracking events with Amplitude
  track(eventName, eventProperties) {
    if (window.Sofatutor?.tracking?.amplitude) {
      window.Sofatutor.tracking.amplitude.track(eventName, eventProperties);
    }
  }

  // API calls for specific consent settings

  // Update chatbot consent setting
  async updateChatbotConsent(userId, value) {
    return makeRequest(
      `/api/sofabuddy/v1/settings/chatbot_consent?user_id=${userId}`,
      'PATCH',
      { value }
    );
  }

  // Update microphone consent setting
  async updateMicrophoneConsent(userId, value) {
    return makeRequest(
      `/api/sofabuddy/v1/settings/microphone_consent?user_id=${userId}`,
      'PATCH',
      { value }
    );
  }

  // Set chatbot consent status via API
  async setChatbotConsent(showPopup = false) {
    try {
      const userId = window.Sofatutor?.user?.id;

      // Always give chatbot consent
      await this.updateChatbotConsent(userId, 'true');

      // Get checkbox state if present
      const micConsentCheckbox = document.querySelector(
        'input[name="microphone_consent"]'
      );

      if (micConsentCheckbox) {
        await this.updateMicrophoneConsent(userId, micConsentCheckbox.checked);
      }

      // Track event if configured
      if (this.amplitudeEventConsent) {
        this.track(this.amplitudeEventConsent, this.eventPropertiesConsent);
      }

      // Show popup or redirect
      if (showPopup && this._hasConsentPopup()) {
        this._openConsentPopup();
      }
    } catch (error) {
      console.error('Error updating consent:', error);
    }
  }

  // Set microphone consent status via API
  async setMicrophoneConsent(value) {
    try {
      const userId = window.Sofatutor?.user?.id;

      // Update consent value
      await this.updateMicrophoneConsent(userId, value);

      // Reload to update UI
      window.location.reload();
    } catch (error) {
      console.error('Error updating microphone consent:', error);
    }
  }

  // Open the consent success popup
  _openConsentPopup() {
    const $successConsentPopup = $('#sofabuddy-success-consent-popup');
    $successConsentPopup.foundation('open');
    this._startProgressBar();
  }

  // Check if consent popup exists in DOM
  _hasConsentPopup() {
    return $('#sofabuddy-success-consent-popup').length > 0;
  }

  // Start progress bar animation
  _startProgressBar() {
    const progressbar = document.querySelector(
      '.js-progress-bar .progress-meter'
    );
    if (!progressbar) return;

    let width = 0;
    const duration = 3000; // 3 seconds
    const interval = 30; // Update every 30ms
    const increment = (interval / duration) * 100;

    const intervalId = setInterval(() => {
      width += increment;
      progressbar.style.width = `${width}%`;

      if (width >= 100) {
        clearInterval(intervalId);

        if (this.redirectPathConsentGiven) {
          window.location.href = this.redirectPathConsentGiven;
        }
      }
    }, interval);
  }
}

export default new SofabuddyConsentService();
